import { FC, memo, ReactNode, useMemo } from 'react';
import { Collapse, Input } from 'antd';
import { capitalCase } from 'change-case';
import {
    TbArrowsSplit,
    TbAward,
    TbBuilding,
    TbBuildingBank,
    TbCalendarSearch,
    TbChartBar,
    TbChevronDown,
    TbDatabaseSearch,
    TbFileAnalytics,
    TbFileText,
    TbHistory,
    TbIdBadge2,
    TbLayoutGrid,
    TbMail,
    TbNews,
    TbRefresh,
    TbSchool,
    TbTags,
    TbTransactionDollar,
    TbUserSearch,
    TbZoomCode,
} from 'react-icons/tb';

import { GmailSVGIcon, MicrosoftOutlookSVGIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useTags } from '@/queries';
import {
    PlatformForm as Form,
    getSearchFilterActiveCount,
    PlatformSelect,
    SearchFilterCheckboxList,
    SearchFilterSectionLabel,
    SearchFilterSectionOverflow,
    SearchFiltersSider,
    unwrapSearchFilters,
    WorkspaceResourceGlyph,
    type SearchFilterChangeHandler,
    type SearchFilterCheckboxOption,
} from '@/shared';
import { isSearchDomainAvailable } from '@/shared/constants';
import { hasGatedFeatureAccess } from '@/shared/entitlements';
import { useBillingStore } from '@/stores/database/aliases/billing';

import CompanyFilterOptions from '../../../02-Companies/01-CompaniesSearch/layout/01-Sider/views/01-FilterOptions';
import SavedFilters from '../../../02-Companies/01-CompaniesSearch/layout/01-Sider/views/02-CustomFilters';
import { getSearchFilterFieldMetadata } from '../../../02-Companies/01-CompaniesSearch/layout/02-FiltersRow/filterFieldMetadata';
import DocumentFilterOptions from '../../../04-Documents/01-DocumentsSearch/01-Sider/views/01-Filters';
import GlobalTradeFilterFields from '../../../07-GlobalTrade/components/GlobalTradeFilterFields';
import { parseGlobalTradeFilterValues } from '../../../07-GlobalTrade/components/GlobalTradeFilterFields/filterValues';
import { ALL_SEARCH_DOMAINS, AllSearchDomain } from '../../useAllSearch';

interface AllSearchFiltersProps {
    domains?: readonly AllSearchDomain[];
    filters: unknown;
    handleChange: SearchFilterChangeHandler;
    presentation?: 'content' | 'sider';
    selectedFilter?: { domain: AllSearchDomain; key: string };
    supplementalContent?: ReactNode;
}

interface FilterSectionDefinition {
    children: ReactNode;
    icon: ReactNode;
    label: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
const recordValue = (value: unknown) => (isRecord(value) ? value : {});
const arrayValue = (value: unknown) => (Array.isArray(value) ? value.map(String) : []);

const checkboxStack = (
    options: SearchFilterCheckboxOption[],
    value: string[],
    onChange: (values: string[]) => void,
) => <SearchFilterCheckboxList onChange={onChange} options={options} value={value} />;

const DomainFilterGroup: FC<{
    label: string;
    presentation?: 'collapse' | 'editor';
    sections: FilterSectionDefinition[];
    selectedSectionKey?: string;
}> = ({ label, presentation = 'collapse', sections, selectedSectionKey }) => {
    const { token } = useAntDesignTheme();

    if (presentation === 'editor') {
        return <>{sections.find((section) => section.label === selectedSectionKey)?.children}</>;
    }

    return (
        <div>
            <small style={{ color: token.colorTextTertiary }}>
                {label} ({sections.length})
            </small>
            <Collapse
                expandIcon={({ isActive }) => (
                    <TbChevronDown
                        size='1rem'
                        style={{
                            color: token.colorTextTertiary,
                            transform: `rotate(${isActive ? '0deg' : '-90deg'})`,
                        }}
                    />
                )}
                expandIconPosition='end'
                items={sections.map((section) => ({
                    children: <SearchFilterSectionOverflow>{section.children}</SearchFilterSectionOverflow>,
                    key: section.label,
                    label: <SearchFilterSectionLabel icon={section.icon} label={section.label} />,
                }))}
                size='small'
                style={{ backgroundColor: token.colorBgBase, borderColor: token.colorBorder, margin: '0.75rem 0' }}
            />
        </div>
    );
};

const AllSearchFilters: FC<AllSearchFiltersProps> = ({
    domains = ALL_SEARCH_DOMAINS,
    filters,
    handleChange,
    presentation = 'sider',
    selectedFilter,
    supplementalContent,
}): JSX.Element => {
    const legacyFilters = useMemo(() => recordValue(unwrapSearchFilters(filters)), [filters]);
    const searchDomains = recordValue(legacyFilters.searchDomains);
    const filterFor = (domain: AllSearchDomain) => recordValue(searchDomains[domain]);
    const changeFor = (domain: AllSearchDomain) => (path: string[], value: unknown) =>
        handleChange(['searchDomains', domain, ...path], value);
    const workspaceFilters = filterFor('workspace');
    const billingSnapshot = useBillingStore((state) => state.snapshot);
    const canUseEmailAndCalendar = hasGatedFeatureAccess(billingSnapshot, 'emailAndCalendar');
    const canUsePeopleEnrichment = hasGatedFeatureAccess(billingSnapshot, 'dataEnrichment');
    const tagsQuery = useTags();
    const peopleFilters = filterFor('people');
    const documentFilters = filterFor('documents');
    const economicFilters = filterFor('economic');
    const newsFilters = filterFor('news');
    const eventFilters = filterFor('events');
    const tradeFilters = filterFor('trade');
    const editorPropsFor = (domain: AllSearchDomain) => ({
        presentation: selectedFilter?.domain === domain ? ('editor' as const) : ('collapse' as const),
        selectedSectionKey: selectedFilter?.domain === domain ? selectedFilter.key : undefined,
    });
    const filterGroups: { domain: AllSearchDomain; content: ReactNode }[] = [
        {
            domain: 'companies',
            content: (
                <CompanyFilterOptions
                    filters={filterFor('companies')}
                    handleChange={changeFor('companies')}
                    {...editorPropsFor('companies')}
                />
            ),
        },
        {
            domain: 'workspace',
            content: (
                <DomainFilterGroup
                    {...editorPropsFor('workspace')}
                    label='Workspace Filters'
                    sections={[
                        {
                            children: checkboxStack(
                                [
                                    {
                                        icon: <WorkspaceResourceGlyph resource='folders' size='1rem' />,
                                        label: 'Folders',
                                        value: 'folders',
                                    },
                                    {
                                        icon: <WorkspaceResourceGlyph resource='investment' size='1rem' />,
                                        label: 'Investments',
                                        value: 'investments',
                                    },
                                    {
                                        icon: <WorkspaceResourceGlyph resource='memo' size='1rem' />,
                                        label: 'Memos',
                                        value: 'memos',
                                    },
                                    {
                                        icon: <WorkspaceResourceGlyph resource='pipeline' size='1rem' />,
                                        label: 'Pipelines',
                                        value: 'pipelines',
                                    },
                                    {
                                        icon: <WorkspaceResourceGlyph resource='team' size='1rem' />,
                                        label: 'Teams',
                                        value: 'teams',
                                    },
                                    {
                                        icon: <WorkspaceResourceGlyph resource='dashboard' size='1rem' />,
                                        label: 'Dashboards',
                                        value: 'dashboards',
                                    },
                                    {
                                        icon: <WorkspaceResourceGlyph resource='list' size='1rem' />,
                                        label: 'Lists',
                                        value: 'lists',
                                    },
                                ],
                                arrayValue(workspaceFilters.resourceTypes),
                                (values) => changeFor('workspace')(['resourceTypes'], values),
                            ),
                            icon: <TbLayoutGrid />,
                            label: 'Resource Types',
                        },
                        {
                            children: (
                                <Form>
                                    <Form.Item label='Tags' style={{ marginBottom: 0 }}>
                                        <PlatformSelect
                                            allowClear
                                            loading={tagsQuery.isLoading}
                                            mode='multiple'
                                            onChange={(values) => changeFor('workspace')(['tags'], values)}
                                            options={(tagsQuery.data ?? []).map((tag) => ({
                                                label: tag.name,
                                                value: tag.id,
                                            }))}
                                            placeholder='Select Tags...'
                                            style={{ width: '100%' }}
                                            value={arrayValue(workspaceFilters.tags)}
                                        />
                                    </Form.Item>
                                </Form>
                            ),
                            icon: <TbTags />,
                            label: 'Tags',
                        },
                        {
                            children: (
                                <Form>
                                    <Form.Item label='Updated Within' style={{ marginBottom: 0 }}>
                                        <PlatformSelect
                                            allowClear
                                            onChange={(value) => changeFor('workspace')(['updatedWithin'], value)}
                                            options={[
                                                { label: 'Past 24 hours', value: 'day' },
                                                { label: 'Past 7 days', value: 'week' },
                                                { label: 'Past 30 days', value: 'month' },
                                                { label: 'Past 90 days', value: 'quarter' },
                                            ]}
                                            placeholder='Any Time...'
                                            style={{ width: '100%' }}
                                            value={workspaceFilters.updatedWithin}
                                        />
                                    </Form.Item>
                                </Form>
                            ),
                            icon: <TbHistory />,
                            label: 'Last Updated',
                        },
                    ]}
                />
            ),
        },
        {
            domain: 'people',
            content: (
                <DomainFilterGroup
                    {...editorPropsFor('people')}
                    label='People Filters'
                    sections={[
                        {
                            children: (
                                <Form>
                                    <Form.Item label='First Name' style={{ marginBottom: '0.5rem' }}>
                                        <Input
                                            onChange={(event) => changeFor('people')(['firstName'], event.target.value)}
                                            placeholder='Jane...'
                                            prefix={<TbUserSearch />}
                                            value={String(peopleFilters.firstName ?? '')}
                                        />
                                    </Form.Item>
                                    <Form.Item label='Last Name' style={{ marginBottom: '0.5rem' }}>
                                        <Input
                                            onChange={(event) => changeFor('people')(['lastName'], event.target.value)}
                                            placeholder='Doe...'
                                            prefix={<TbUserSearch />}
                                            value={String(peopleFilters.lastName ?? '')}
                                        />
                                    </Form.Item>
                                    <Form.Item label='Email Address' style={{ marginBottom: 0 }}>
                                        <Input
                                            onChange={(event) => changeFor('people')(['email'], event.target.value)}
                                            placeholder='jane@example.com...'
                                            prefix={<TbMail />}
                                            value={String(peopleFilters.email ?? '')}
                                        />
                                    </Form.Item>
                                </Form>
                            ),
                            icon: <TbIdBadge2 />,
                            label: 'Name / Email Address',
                        },
                        {
                            children: (
                                <Form>
                                    <Form.Item label='Job Titles' style={{ marginBottom: '0.5rem' }}>
                                        <PlatformSelect
                                            allowClear
                                            mode='tags'
                                            onChange={(values) => changeFor('people')(['jobTitles'], values)}
                                            placeholder='Add Job Titles...'
                                            options={
                                                getSearchFilterFieldMetadata('searchDomains.people.jobTitles')?.options
                                            }
                                            style={{ width: '100%' }}
                                            value={arrayValue(peopleFilters.jobTitles)}
                                        />
                                    </Form.Item>
                                    <Form.Item label='Functional Roles' style={{ marginBottom: 0 }}>
                                        <PlatformSelect
                                            allowClear
                                            mode='multiple'
                                            onChange={(values) => changeFor('people')(['roles'], values)}
                                            options={[
                                                'accounting',
                                                'education',
                                                'engineering',
                                                'finance',
                                                'health',
                                                'human_resources',
                                                'legal',
                                                'marketing',
                                                'operations',
                                                'real_estate',
                                                'sales',
                                            ].map((value) => ({
                                                label: capitalCase(value),
                                                value,
                                            }))}
                                            placeholder='Select Roles...'
                                            style={{ width: '100%' }}
                                            value={arrayValue(peopleFilters.roles)}
                                        />
                                    </Form.Item>
                                </Form>
                            ),
                            icon: <TbAward />,
                            label: 'Job Title / Role',
                        },
                        {
                            children: (
                                <Form>
                                    <Form.Item label='Education Levels' style={{ marginBottom: 0 }}>
                                        <PlatformSelect
                                            allowClear
                                            mode='multiple'
                                            onChange={(values) => changeFor('people')(['educationLevels'], values)}
                                            options={[
                                                'high_school',
                                                'associate',
                                                'bachelors',
                                                'masters',
                                                'doctorate',
                                            ].map((value) => ({ label: capitalCase(value), value }))}
                                            placeholder='Select Education Levels...'
                                            style={{ width: '100%' }}
                                            value={arrayValue(peopleFilters.educationLevels)}
                                        />
                                    </Form.Item>
                                </Form>
                            ),
                            icon: <TbSchool />,
                            label: 'Education Level',
                        },
                        {
                            children: (
                                <Form>
                                    <Form.Item label='Skills and Keywords' style={{ marginBottom: 0 }}>
                                        <PlatformSelect
                                            allowClear
                                            mode='tags'
                                            onChange={(values) => changeFor('people')(['keywords'], values)}
                                            placeholder='Add Skills And Keywords...'
                                            style={{ width: '100%' }}
                                            value={arrayValue(peopleFilters.keywords)}
                                        />
                                    </Form.Item>
                                </Form>
                            ),
                            icon: <TbZoomCode />,
                            label: 'Keywords',
                        },
                        {
                            children: checkboxStack(
                                [
                                    {
                                        avatar: { src: GmailSVGIcon },
                                        description: canUseEmailAndCalendar
                                            ? undefined
                                            : 'Requires the Relations add-on',
                                        disabled: !canUseEmailAndCalendar,
                                        label: 'Gmail',
                                        value: 'Gmail',
                                    },
                                    {
                                        avatar: { src: MicrosoftOutlookSVGIcon },
                                        description: canUseEmailAndCalendar
                                            ? undefined
                                            : 'Requires the Relations add-on',
                                        disabled: !canUseEmailAndCalendar,
                                        label: 'Microsoft 365 Outlook',
                                        value: 'Microsoft 365 Outlook',
                                    },
                                    {
                                        avatar: { website: 'peopledatalabs.com' },
                                        description: canUsePeopleEnrichment
                                            ? undefined
                                            : 'Requires Premium or the Relations add-on',
                                        disabled: !canUsePeopleEnrichment,
                                        label: 'People Data Labs Enrichment',
                                        value: 'enrichment',
                                    },
                                    {
                                        icon: <TbDatabaseSearch />,
                                        label: 'Platform Financials Executives',
                                        value: 'fmpExecutives',
                                    },
                                ],
                                arrayValue(peopleFilters.sources),
                                (values) => changeFor('people')(['sources'], values),
                            ),
                            icon: <TbUserSearch />,
                            label: 'Connected & Enriched Sources',
                        },
                    ]}
                />
            ),
        },
        {
            domain: 'documents',
            content: (
                <>
                    <DocumentFilterOptions
                        filters={documentFilters}
                        handleChange={changeFor('documents')}
                        {...editorPropsFor('documents')}
                    />
                    <DomainFilterGroup
                        {...editorPropsFor('documents')}
                        label='Document Coverage'
                        sections={[
                            {
                                children: checkboxStack(
                                    [
                                        {
                                            icon: <TbDatabaseSearch />,
                                            label: 'Connected Documents',
                                            value: 'connected',
                                        },
                                        {
                                            icon: <TbBuildingBank />,
                                            label: 'Public Company Filings',
                                            value: 'publicFilings',
                                        },
                                    ],
                                    arrayValue(documentFilters.types),
                                    (values) => changeFor('documents')(['types'], values),
                                ),
                                icon: <TbFileText />,
                                label: 'Document Types',
                            },
                        ]}
                    />
                </>
            ),
        },
        {
            domain: 'economic',
            content: (
                <DomainFilterGroup
                    {...editorPropsFor('economic')}
                    label='Economic Data Filters'
                    sections={[
                        {
                            children: checkboxStack(
                                [
                                    {
                                        icon: <TbTransactionDollar />,
                                        label: 'Macroeconomic Indicators',
                                        value: 'macro',
                                    },
                                    {
                                        icon: <TbBuildingBank />,
                                        label: 'US Treasury Rates',
                                        value: 'treasury',
                                    },
                                    {
                                        icon: <TbChartBar />,
                                        label: 'Markets, Commodities, Forex & Crypto',
                                        value: 'market',
                                    },
                                ],
                                arrayValue(economicFilters.categories),
                                (values) => changeFor('economic')(['categories'], values),
                            ),
                            icon: <TbTransactionDollar />,
                            label: 'Dataset Categories',
                        },
                        {
                            children: checkboxStack(
                                [
                                    {
                                        icon: <TbDatabaseSearch />,
                                        label: 'Platform Financials',
                                        value: 'fmp',
                                    },
                                    {
                                        avatar: { website: 'fred.stlouisfed.org' },
                                        label: 'FRED',
                                        value: 'fred',
                                    },
                                ],
                                arrayValue(economicFilters.sources),
                                (values) => changeFor('economic')(['sources'], values),
                            ),
                            icon: <TbDatabaseSearch />,
                            label: 'Sources',
                        },
                        {
                            children: (
                                <Form>
                                    <Form.Item label='Reporting Frequencies' style={{ marginBottom: 0 }}>
                                        <PlatformSelect
                                            allowClear
                                            mode='multiple'
                                            onChange={(values) => changeFor('economic')(['frequencies'], values)}
                                            options={
                                                getSearchFilterFieldMetadata('searchDomains.economic.frequencies')
                                                    ?.options
                                            }
                                            placeholder='Select Frequencies...'
                                            style={{ width: '100%' }}
                                            value={arrayValue(economicFilters.frequencies)}
                                        />
                                    </Form.Item>
                                </Form>
                            ),
                            icon: <TbRefresh />,
                            label: 'Frequency',
                        },
                    ]}
                />
            ),
        },
        {
            domain: 'trade',
            content: (
                <GlobalTradeFilterFields
                    filters={parseGlobalTradeFilterValues(tradeFilters)}
                    onChange={(key, value) => changeFor('trade')([key], value)}
                    {...editorPropsFor('trade')}
                />
            ),
        },
        {
            domain: 'news',
            content: (
                <DomainFilterGroup
                    {...editorPropsFor('news')}
                    label='News Filters'
                    sections={[
                        {
                            children: checkboxStack(
                                [
                                    { icon: <TbNews />, label: 'Mainstream', value: 'Mainstream' },
                                    { icon: <TbFileText />, label: 'Press Releases', value: 'Press Releases' },
                                    { icon: <TbTransactionDollar />, label: 'Currencies', value: 'Currencies' },
                                    { icon: <TbChartBar />, label: 'Cryptocurrencies', value: 'Cryptocurrencies' },
                                ],
                                arrayValue(newsFilters.feeds),
                                (values) => changeFor('news')(['feeds'], values),
                            ),
                            icon: <TbNews />,
                            label: 'News Feeds',
                        },
                        {
                            children: checkboxStack(
                                [
                                    { icon: <TbNews />, label: 'News', value: 'market' },
                                    {
                                        icon: <TbTransactionDollar />,
                                        label: 'Insider Transactions',
                                        value: 'insiderTransactions',
                                    },
                                    { icon: <TbBuilding />, label: 'IPOs', value: 'ipos' },
                                    { icon: <TbArrowsSplit />, label: 'M&A', value: 'mAndA' },
                                    {
                                        icon: <TbChartBar />,
                                        label: 'Fundraising & Crowdfunding',
                                        value: 'fundraising',
                                    },
                                ],
                                arrayValue(newsFilters.types),
                                (values) => changeFor('news')(['types'], values),
                            ),
                            icon: <TbFileAnalytics />,
                            label: 'Coverage Types',
                        },
                    ]}
                />
            ),
        },
        {
            domain: 'events',
            content: (
                <DomainFilterGroup
                    {...editorPropsFor('events')}
                    label='Event Filters'
                    sections={[
                        ...['Coverage', 'Countries', 'Currencies', 'Dividend Symbols'].map((label, index) => {
                            const key = ['coverage', 'countries', 'currencies', 'symbols'][index];
                            const metadata = getSearchFilterFieldMetadata(`searchDomains.events.${key}`);
                            return {
                                label,
                                icon: <TbCalendarSearch />,
                                children: (
                                    <Form>
                                        <Form.Item label={label} style={{ marginBottom: 0 }}>
                                            <PlatformSelect
                                                aria-label={label}
                                                mode={key === 'coverage' ? undefined : 'tags'}
                                                options={metadata?.options}
                                                value={
                                                    key === 'coverage'
                                                        ? String(eventFilters.coverage ?? 'research')
                                                        : arrayValue(eventFilters[key])
                                                }
                                                onChange={(value) => changeFor('events')([key], value)}
                                                placeholder={`Select ${label}...`}
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                    </Form>
                                ),
                            };
                        }),
                        {
                            children: checkboxStack(
                                [
                                    {
                                        icon: <TbCalendarSearch />,
                                        label: 'Dividend Calendar',
                                        value: 'dividend',
                                    },
                                    {
                                        icon: <TbTransactionDollar />,
                                        label: 'Economic Calendar',
                                        value: 'economic',
                                    },
                                ],
                                Array.isArray(eventFilters.sources)
                                    ? arrayValue(eventFilters.sources)
                                    : eventFilters.coverage === 'all'
                                      ? ['dividend', 'economic']
                                      : ['economic'],
                                (values) => changeFor('events')(['sources'], values.length ? values : ['none']),
                            ),
                            icon: <TbCalendarSearch />,
                            label: 'Calendar Sources',
                        },
                        {
                            children: checkboxStack(
                                [
                                    { icon: <TbChartBar />, label: 'High Impact', value: 'high' },
                                    { icon: <TbChartBar />, label: 'Medium Impact', value: 'medium' },
                                    { icon: <TbChartBar />, label: 'Low Impact', value: 'low' },
                                ],
                                arrayValue(eventFilters.impacts),
                                (values) => changeFor('events')(['impacts'], values),
                            ),
                            icon: <TbBuilding />,
                            label: 'Economic Impact',
                        },
                    ]}
                />
            ),
        },
    ];
    const visibleGroups = domains.filter(isSearchDomainAvailable).flatMap((domain) => {
        const group = filterGroups.find((candidate) => candidate.domain === domain);
        return group ? [group] : [];
    });
    const filtersView = (
        <div>
            {visibleGroups.map(({ content, domain }) => (
                <div key={domain}>{content}</div>
            ))}
            {supplementalContent}
        </div>
    );

    if (presentation === 'content') {
        return filtersView;
    }

    return (
        <SearchFiltersSider
            activeFilterCount={getSearchFilterActiveCount(searchDomains)}
            filters={filters}
            filtersView={filtersView}
            savedFiltersView={<SavedFilters />}
        />
    );
};

export default memo(AllSearchFilters);
